import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { DealCycleApiService } from '../services/deal-cycle-api.service';
import {
  DealCycleRequest,
  DealCycleResponse,
} from '../models/deal-cycle.model';
import { PreventSpecialCharactersDirective } from '../../../shared/directives/prevent-special-characters.directive';
import { PreventLongNamesDirective } from '../../../shared/directives/prevent-long-names.directive';
import { PreventLongOrdersDirective } from '../../../shared/directives/prevent-long-orders.directive';
import { ValidatorFormsInformation } from '../../../shared/utils/validator-forms-information.utils';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-deal-cycle-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    PreventSpecialCharactersDirective,
    PreventLongNamesDirective,
    PreventLongOrdersDirective,
  ],
  templateUrl: './deal-cycle-form.component.html',
})
export class DealCycleFormComponent implements OnInit {
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  itemId: number | null = null;

  // Form fields
  name = '';
  private originalName = '';
  order = 0;
  closeRate = 0;
  color = '';

  dealCyclesExisting: DealCycleResponse[] = [];

  constructor(
    private api: DealCycleApiService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.itemId = +id;
      this.cargar(this.itemId);
    }
    this.getDealCycleExisting();
  }

  getDealCycleExisting() {
    this.api.getAll().subscribe({
      next: (dealCycles) => {
        this.dealCyclesExisting = dealCycles ?? [];
      },
      error: (err) => {
        this.toast.error({
          title: 'Etapas de pipeline',
          message:
            err.error?.message || 'No se pudo obtener las etapas existentes',
        });
      },
    });
  }

  preventWrongRange(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;

    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
      'Home',
      'End',
    ];

    if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      return;
    }

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    if (event.key === '-') {
      event.preventDefault();
      return;
    }

    if (Number(input.value) > 100) {
      event.preventDefault();
      this.closeRate = 100;
      this.error = 'La probabilidad de cierre no puede ser mayor al 100%';
    }
    if (Number(input.value) < 0) {
      event.preventDefault();
      this.closeRate = 0;
      this.error = 'La probabilidad de cierre no puede ser inferior a 0%';
    }
  }

  setError(message: string) {
    this.error = message;
  }

  private cargar(id: number): void {
    this.loading = true;
    this.error = '';

    this.api.getById(id).subscribe({
      next: (item) => {
        this.name = item.name;
        this.originalName = item.name;
        this.order = item.order;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar la etapa';
        this.loading = false;
      },
    });
  }

  get pageTitle(): string {
    return this.isEditing
      ? 'Editar Etapa del Pipeline'
      : 'Nueva Etapa del Pipeline';
  }

  guardar(): void {
    const nameValidation = ValidatorFormsInformation.validateText(
      this.name,
      'nombre',
      30,
    );

    if (nameValidation) {
      this.error = nameValidation;
      this.toast.warning({ title: 'Datos no válidos', message: this.error });
      return;
    }

    const orderValidation = ValidatorFormsInformation.validateNumber(
      this.order,
      'orden',
      4,
    );

    if (orderValidation) {
      this.error = orderValidation;
      this.toast.warning({ title: 'Datos no válidos', message: this.error });
      return;
    }

    if (
      this.dealCyclesExisting.some(
        (dealCycle) => dealCycle.order === this.order,
      )
    ) {
      this.error = 'La orden ya se encuentra en uso';
      this.toast.warning({ title: 'Orden duplicado', message: this.error });
      return;
    }

    if (
      this.dealCyclesExisting.some(
        (dealCycle) => dealCycle.name.toLowerCase() === this.name.toLowerCase(),
      )
    ) {
      this.error = 'El nombre ya se encuentra en uso';
      this.toast.warning({ title: 'Nombre duplicado', message: this.error });
      return;
    }
    if (this.closeRate > 100) {
      this.error = 'La probabilidad de cierre no puede ser mayor al 100%';
      return;
    }

    if (this.closeRate < 0) {
      this.error = 'La probabilidad de cierre no puede ser inferior a 0%';
      return;
    }

    if (this.closeRate > 100) {
      this.error = 'La probabilidad de cierre no puede ser mayor al 100%';
      return;
    }
    if (!(this.closeRate >= 0 && this.closeRate <= 100)) {
      this.error = 'La probabilidad solamente puede ser entre 0% y 100%';
      return;
    }

    this.saving = true;
    this.error = '';

    const request: DealCycleRequest = {
      name: this.name.trim(),
      order: this.order,
      closeRate: this.closeRate,
      color: this.color,
    };

    if (this.isEditing && this.itemId) {
      this.api.update(this.itemId, request).subscribe({
        next: () => {
          this.saving = false;
          this.router.navigate(['/config-deals/ciclos']);
          this.toast.updated('Etapa del pipeline',
            this.originalName !== this.name.trim()
              ? `Se actualizó "${this.originalName}" → "${this.name.trim()}".`
              : `Se actualizó "${this.name.trim()}" correctamente.`);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al actualizar la etapa';
          this.toast.error({ title: 'Error', message: this.error });
        },
      });
    } else {
      this.api.create(request).subscribe({
        next: () => {
          this.saving = false;
          this.router.navigate(['/config-deals/ciclos']);
          this.toast.created('Etapa del pipeline', `Se creó "${this.name.trim()}" correctamente.`);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al crear la etapa';
          this.toast.error({ title: 'Error', message: this.error });
        },
      });
    }
  }

  cancelar(): void {
    this.router.navigate(['/config-deals/ciclos']);
  }
}
